/**
 * Which ward the list is showing.
 *
 * A native `<select>`, deliberately. A combobox with search would be a dependency to justify
 * under AGENTS.md §8 for a small hospital's handful of wards, and the native control is the one
 * that already works with a keyboard, with a screen reader, and under a gloved finger on a ward
 * tablet.
 *
 * It asks for the directory itself rather than taking it as a prop. `App` does not need to know
 * which wards exist — it only needs to remember which one was picked, because that has to
 * survive opening a patient and pressing Back.
 *
 * The asking is `useWardDirectory`, shared with the registration form's ward field since #47 gave
 * that screen a choice too. Only the fetch policy is shared, and that module says why.
 */

import { useWardDirectory } from './wardDirectory'
import type { WardTranslate } from './wardDisplay'

/**
 * The ward picker.
 *
 * @param selected The ward code currently shown, or null before the directory has answered.
 * @param onSelect Called with the code to show — by a clinician's choice, or once on load.
 * @param t The ward list's lookup, passed in rather than imported (see `wardDisplay.ts`).
 */
export function WardPicker({
  selected,
  onSelect,
  t,
}: {
  readonly selected: string | null
  readonly onSelect: (ward: string) => void
  readonly t: WardTranslate
}) {
  // Null for both "still asking" and "could not ask", because this component renders the same
  // thing for either: nothing. The ward list underneath is reporting on the same request to the
  // same box, and two notices for one unreachable box read as two faults. The first ward is
  // picked for the clinician in there, so the start screen shows a list rather than an empty
  // frame with a dropdown on it (PHASE-0.md §2.2).
  const wards = useWardDirectory(selected, onSelect)

  // Nothing to show until the directory has answered — and nothing to show when it holds one
  // ward, because one ward is not a choice. The heading already names it, which is what a
  // clinician checks before trusting the list, and a box seeded before #41 is in exactly this
  // shape. The registration form makes the opposite call for one ward, because there the control
  // is also the statement of where the patient is going.
  if (wards === null || wards.length <= 1) {
    return null
  }

  return (
    <p className="ward__picker">
      <label htmlFor="ward-picker">{t('ward:picker.label')}</label>{' '}
      <select
        id="ward-picker"
        value={selected ?? ''}
        onChange={(event) => onSelect(event.target.value)}
      >
        {wards.map((ward) => (
          // The code and the name both, because staff say the code and the printed sheet
          // carries it. Assembled here rather than through the catalogue: `name` is record
          // content and must not pass through a translated message (AGENTS.md §7).
          <option key={ward.ward} value={ward.ward}>
            {ward.ward} — {ward.name}
          </option>
        ))}
      </select>
    </p>
  )
}
